import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import Button from "../../components/ui/Button";
import CartItem from "./components/CartItem";
import CartSummary from "./components/CartSummary";
import CheckoutModal from "./components/CheckoutModal";
import LiveDataStreamingSvc from "./components/LiveDataStreamingSvc";
import EmptyCart from "./components/EmptyCart";
import FloatigBoxDemo from "./components/FloatigBoxDemo";
import PromiseProblemDemo from "./components/PromiseProblemDemo";
import VideoPlayerDemo from "./components/VideoPlayerDemo";
import APIUsecaseDemo from "./components/APIUsecaseDemo";

import {
  setCartItems,
  setCouponCode,
  setAppliedCoupon,
  updateQuantity,
  removeItem,
  clearCart,
  selectCartItems,
  selectCouponCode,
  selectAppliedCoupon,
  selectSubtotal,
  selectTax,
  selectShipping,
  selectDiscount,
  selectTotal,
} from "../../redux/slices/cartSlice";

const ShoppingCartManagement = () => {
  const dispatch = useDispatch();

  const cartItems = useSelector(selectCartItems);
  const couponCode = useSelector(selectCouponCode);
  const appliedCoupon = useSelector(selectAppliedCoupon);
  const subtotal = useSelector(selectSubtotal);
  const tax = useSelector(selectTax);
  const shipping = useSelector(selectShipping);
  const discount = useSelector(selectDiscount);
  const total = useSelector(selectTotal);

  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const handleUpdateQuantity = (itemId, newQuantity) => {
    dispatch(updateQuantity({ id: itemId, quantity: newQuantity }));
  };

  const handleRemoveItem = (itemId) => {
    dispatch(removeItem(itemId));
  };

  const handleApplyCoupon = () => {
    const validCoupons = ["SAVE10", "SAVE20", "FREESHIP"];
    if (validCoupons?.includes(couponCode?.toUpperCase())) {
      dispatch(setAppliedCoupon(couponCode?.toUpperCase()));
    } else {
      alert("Invalid coupon code");
    }
  };

  const handleCouponChange = (val) => {
    dispatch(setCouponCode(val));
  };

  const handleCheckout = () => {
    setIsCheckoutOpen(true);
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-8 md:py-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 md:mb-3">
            Shopping Cart
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Review your items and proceed to checkout
          </p>
        </div>

        {cartItems?.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-1 space-y-4 md:space-y-6">
              <div className="bg-card border border-border rounded-lg p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-semibold text-foreground">
                    Cart Items ({cartItems?.length})
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    iconPosition="left"
                    onClick={() => dispatch(clearCart())}
                    className="text-error"
                  >
                    Clear All
                  </Button>
                </div>

                <div className="space-y-4 md:space-y-6">
                  {cartItems?.map((item) => (
                    <CartItem
                      key={item?.id}
                      item={item}
                      onUpdateQuantity={handleUpdateQuantity}
                      onRemove={handleRemoveItem}
                    />
                  ))}
                </div>
              </div>

              <LiveDataStreamingSvc />

              <FloatigBoxDemo />

              <PromiseProblemDemo />

              <VideoPlayerDemo />

              <APIUsecaseDemo />
            </div>

            <CartSummary
              subtotal={subtotal}
              tax={tax}
              shipping={shipping}
              discount={discount}
              total={total}
              couponCode={couponCode}
              onCouponChange={handleCouponChange}
              onApplyCoupon={handleApplyCoupon}
              onCheckout={handleCheckout}
              itemCount={cartItems?.reduce(
                (sum, item) => sum + item?.quantity,
                0,
              )}
            />
          </div>
        )}
      </div>

      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        total={total}
      />
    </div>
  );
};

export default ShoppingCartManagement;
